import { formationById, playerById, techniqueById } from '../data';
import matchAiConfig from '../../../data/config/match_ai.json';
import type { EnginePlayer, EngineTeam, EngineTechnique, PlayerStats } from '../engine/data/EngineData';
import { MatchEngine, type EngineMatchResult, type MatchSetup } from '../engine/match/MatchEngine';
import { mapArchetypeToPassive } from '../engine/ai/PassiveSystem';
import { normaliseAssignedRole, tacticalRoleForFormationSlot } from '../engine/ai/TacticalRoleSystem';
import type { EngineMatchEvent, EngineMatchEventType } from '../engine/match/MatchEvent';
import type { EngineMatchSnapshot } from '../engine/match/MatchState';
import type { ElementType, Formation, MatchEvent, MatchResult, Player, TacticalRole, Team, Technique, WorldFrame } from '../types';

const PRESENTABLE_EVENTS: Partial<Record<EngineMatchEventType, MatchEvent['type']>> = {
  PASS_RECEIVED: 'PASS',
  PASS_FAILED: 'PASS',
  INTERCEPTION: 'TACKLE',
  DRIBBLE: 'DRIBBLE',
  TACKLE: 'TACKLE',
  SHOT: 'SHOT',
  SAVE: 'SAVE',
  DEFLECTION: 'DEFLECTION',
  REBOUND: 'REBOUND',
  GOAL: 'GOAL',
  TECHNIQUE: 'TECHNIQUE',
  FURY_STARTED: 'FURY',
  FURY_ENDED: 'FURY',
  SUBSTITUTION: 'SUBSTITUTION',
  OUT: 'OUT',
  THROW_IN: 'THROW_IN',
  CORNER: 'CORNER',
  GOAL_KICK: 'GOAL_KICK',
  OFFSIDE: 'OFFSIDE',
  FOUL: 'FOUL',
};

const numberStat = (player: Player, key: string, fallback = 50): number => {
  const value = player.stats[key];
  return Number.isFinite(value) ? value : fallback;
};

export function adaptPlayer(player: Player, matchPlayerId = player.id, assignedRole?: TacticalRole | null): EnginePlayer {
  const metadata = player.metadata ?? {};
  const stats: PlayerStats = {
    kick: numberStat(player, 'kick'),
    dribble: numberStat(player, 'dribble'),
    pass: numberStat(player, 'pass'),
    defense: numberStat(player, 'defense'),
    speed: numberStat(player, 'speed'),
    power: numberStat(player, 'power'),
    control: numberStat(player, 'control'),
    stamina: numberStat(player, 'stamina'),
  };
  return {
    id: matchPlayerId,
    sourcePlayerId: player.id,
    fullName: player.name,
    alias: metadata.nickname?.trim() || player.name.split(' ')[0],
    position: player.position,
    element: player.element,
    passive: mapArchetypeToPassive(metadata.archetype),
    tacticalProfile: { assignedRole: normaliseAssignedRole(player.position, assignedRole) },
    stats,
    tpMax: player.tp_max,
    equippedTechniques: player.techniques,
  };
}

export function adaptTechnique(technique: Technique): EngineTechnique {
  const source = technique as Technique & { requirements?: { minimum_tp?: number } };
  return {
    id: technique.id,
    name: technique.name,
    type: technique.type === 'SAVE' ? 'CATCH' : technique.type,
    element: (technique.element as string) === 'VOID' ? 'VOID' : technique.element,
    power: technique.power,
    tpCost: technique.tp_cost,
    minTp: source.requirements?.minimum_tp ?? technique.tp_cost,
  };
}

export function adaptTeam(team: Team, side: 0 | 1): EngineTeam {
  const formation: Formation | undefined = team.formation ?? formationById.get(team.formation_id);
  if (!formation) throw new Error(`Unknown formation: ${team.formation_id}`);
  const players = team.lineup.map((id, index) => {
    const player = team.playerOverrides?.[id] ?? playerById.get(id);
    if (!player) throw new Error(`Unknown player in lineup: ${id}`);
    // The same catalog identity can be copied by the manager and still appear
    // for a rival. The engine must reason about two distinct footballers even
    // though presentation intentionally shows the same catalog portrait.
    return adaptPlayer(
      player,
      `side-${side}-slot-${index}-${player.id}`,
      team.tacticalRoleOverrides?.[player.id] ?? tacticalRoleForFormationSlot(player.position, formation.roles[index]),
    );
  });
  if (players.length !== 11) throw new Error(`Team ${team.id} requires exactly 11 players`);
  return {
    id: team.id,
    name: team.name,
    side,
    tactics: {
      ...team.tactics,
      defensiveLine: team.tactics.defensiveLine ?? team.tactics.defensive_line ?? 0.5,
      risk: team.tactics.risk ?? 0.5,
      techniqueAggression: team.tactics.techniqueAggression ?? team.tactics.technique_aggression ?? 0.5,
    },
    players,
    roles: [...formation.roles],
    formationPositions: formation.positions.map(([x, y]) => ({ x, y })),
  };
}

export const WEB_MATCH_ENGINE_CONFIG = {
  durationSeconds: matchAiConfig.clock.duration_seconds,
  tpRegenerationPerMinute: matchAiConfig.clock.tp_regeneration_per_minute,
  movementStepSeconds: matchAiConfig.clock.movement_step_seconds,
  decisionIntervalSeconds: matchAiConfig.clock.decision_interval_seconds,
  snapshotIntervalSeconds: matchAiConfig.clock.snapshot_interval_seconds,
  minPlayerSpeed: matchAiConfig.movement.min_player_speed,
  maxPlayerSpeed: matchAiConfig.movement.max_player_speed,
  speedAttributeExponent: matchAiConfig.movement.speed_attribute_exponent,
  acceleration: matchAiConfig.movement.acceleration,
  minimumTurnRateRadiansPerSecond: matchAiConfig.movement.minimum_turn_rate_radians_per_second,
  maximumTurnRateRadiansPerSecond: matchAiConfig.movement.maximum_turn_rate_radians_per_second,
  brakingAccelerationMultiplier: matchAiConfig.movement.braking_acceleration_multiplier,
  minimumReactionSeconds: matchAiConfig.movement.minimum_reaction_seconds,
  maximumReactionSeconds: matchAiConfig.movement.maximum_reaction_seconds,
  passBallSpeed: matchAiConfig.movement.pass_ball_speed,
  shotBallSpeed: matchAiConfig.movement.shot_ball_speed,
  looseBallFriction: matchAiConfig.movement.loose_ball_friction,
  interceptionRadius: matchAiConfig.movement.interception_radius,
  controlRadius: matchAiConfig.movement.control_radius,
  minimumTeamShotIntervalSeconds: matchAiConfig.movement.minimum_team_shot_interval_seconds,
  minimumTeamTackleIntervalSeconds: matchAiConfig.movement.minimum_team_tackle_interval_seconds,
  minimumTeamTechniqueIntervalSeconds: matchAiConfig.movement.minimum_team_technique_interval_seconds,
  minimumPlayerTechniqueIntervalSeconds: matchAiConfig.movement.minimum_player_technique_interval_seconds,
  passActionCooldownSeconds: matchAiConfig.movement.pass_action_cooldown_seconds,
  receptionActionCooldownSeconds: matchAiConfig.movement.reception_action_cooldown_seconds,
  crossReceptionActionCooldownSeconds: matchAiConfig.movement.cross_reception_action_cooldown_seconds,
  receptionControlThreshold: matchAiConfig.movement.reception_control_threshold,
  dribbleActionCooldownSeconds: matchAiConfig.movement.dribble_action_cooldown_seconds,
  shotActionCooldownSeconds: matchAiConfig.movement.shot_action_cooldown_seconds,
  passPreparationSeconds: matchAiConfig.movement.pass_preparation_seconds,
  dribblePreparationSeconds: matchAiConfig.movement.dribble_preparation_seconds,
  shotPreparationSeconds: matchAiConfig.movement.shot_preparation_seconds,
  techniquePreparationSeconds: matchAiConfig.movement.technique_preparation_seconds,
  recentActionMemorySize: matchAiConfig.movement.recent_action_memory_size,
  repeatedPassPairLimit: matchAiConfig.movement.repeated_pass_pair_limit,
  repeatedPassPairPenalty: matchAiConfig.movement.repeated_pass_pair_penalty,
  repeatedActionCyclePenalty: matchAiConfig.movement.repeated_action_cycle_penalty,
  stagnationPassLimit: matchAiConfig.movement.stagnation_pass_limit,
  stagnationMinimumProgress: matchAiConfig.movement.stagnation_minimum_progress,
  stagnationNonProgressivePenalty: matchAiConfig.movement.stagnation_non_progressive_penalty,
  stagnationDribbleBonus: matchAiConfig.movement.stagnation_dribble_bonus,
  transitionWindowSeconds: matchAiConfig.movement.transition_window_seconds,
  defensiveCoverDistance: matchAiConfig.movement.defensive_cover_distance,
  markingDistance: matchAiConfig.movement.marking_distance,
  intentMaximumDurationSeconds: matchAiConfig.movement.intent_maximum_duration_seconds,
  returnToShapeMinimumSeconds: matchAiConfig.movement.return_to_shape_minimum_seconds,
  anchorSoftness: matchAiConfig.movement.anchor_softness,
  reachableTargetHorizonSeconds: matchAiConfig.movement.reachable_target_horizon_seconds,
  emergencyClearPressure: matchAiConfig.movement.emergency_clear_pressure,
  closeRangeShotDistance: matchAiConfig.movement.close_range_shot_distance,
  minimumFinishingShotIntervalSeconds: matchAiConfig.movement.minimum_finishing_shot_interval_seconds,
  minimumDribbleGoalDistance: matchAiConfig.movement.minimum_dribble_goal_distance,
  crossFinishWindowSeconds: matchAiConfig.movement.cross_finish_window_seconds,
  maximumForwardRunProgress: matchAiConfig.movement.maximum_forward_run_progress,
  recoveryWinnerCooldownSeconds: matchAiConfig.movement.recovery_winner_cooldown_seconds,
  recoveryLoserCooldownSeconds: matchAiConfig.movement.recovery_loser_cooldown_seconds,
  dribbleBypassSeconds: matchAiConfig.movement.dribble_bypass_seconds,
  techniqueDribbleBypassSeconds: matchAiConfig.movement.technique_dribble_bypass_seconds,
  tackleStaggerSeconds: matchAiConfig.movement.tackle_stagger_seconds,
  techniqueBlockStaggerSeconds: matchAiConfig.movement.technique_block_stagger_seconds,
  duelFoulProbability: matchAiConfig.movement.duel_foul_probability,
  utilityAi: matchAiConfig.utility_ai,
  furyDurationSeconds: matchAiConfig.fury.duration_seconds,
  furyStatMultiplier: matchAiConfig.fury.stat_multiplier,
  furyMaximumEffectiveStat: matchAiConfig.fury.maximum_effective_stat,
  furyStaminaDrainMultiplier: matchAiConfig.fury.stamina_drain_multiplier,
  furyMinimumActivationMinute: matchAiConfig.fury.minimum_activation_minute,
  furyForcedActivationMinute: matchAiConfig.fury.forced_activation_minute,
  furyActivationUtilityThreshold: matchAiConfig.fury.activation_utility_threshold,
  elementAdvantage: matchAiConfig.elements.advantage,
  elementNeutral: matchAiConfig.elements.neutral,
  elementDisadvantage: matchAiConfig.elements.disadvantage,
} as const;

export function createMatchSetup(home: Team, away: Team): MatchSetup {
  const techniques = new Map<string, EngineTechnique>();
  for (const technique of techniqueById.values()) techniques.set(technique.id, adaptTechnique(technique));
  return { home: adaptTeam(home, 0), away: adaptTeam(away, 1), techniques };
}

export function simulateMatch(home: Team, away: Team, seed: number): MatchResult {
  const engine = new MatchEngine(createMatchSetup(home, away), seed, WEB_MATCH_ENGINE_CONFIG);
  return adaptCompleteResult(engine.run(), home, away, seed);
}

export interface LiveFuryState {
  active: boolean;
  used: boolean;
  remainingSeconds: number;
}

export interface LivePlayerTpState {
  /** Current in-match TP after technique costs and passive regeneration. */
  current: number;
  max: number;
}

export class LiveMatchSession {
  private readonly engine: MatchEngine;
  private readonly events: MatchEvent[] = [];
  private readonly frames: WorldFrame[] = [];
  private eventCursor = 0;
  private snapshotCursor = 0;

  constructor(
    private readonly home: Team,
    private readonly away: Team,
    readonly seed: number,
    controlledSide: 0 | 1,
  ) {
    const opponentSide = controlledSide === 0 ? 1 : 0;
    this.engine = new MatchEngine(createMatchSetup(home, away), seed, WEB_MATCH_ENGINE_CONFIG, {
      autoFurySides: [opponentSide],
    });
    this.synchronizePresentation();
  }

  advanceTo(second: number): MatchResult {
    this.engine.advanceTo(second);
    return this.getResult();
  }

  activateFury(side: 0 | 1): boolean {
    const activated = Boolean(this.engine.activateFury(side));
    if (activated) this.synchronizePresentation();
    return activated;
  }

  /** Replaces one controlled on-pitch catalog player with a bench player. */
  substitute(side: 0 | 1, outgoingPlayerId: string, incomingPlayer: Player): boolean {
    const runtimePlayer = this.engine.state.players.find((player) => player.teamSide === side
      && (player.sourcePlayerId ?? player.playerId) === outgoingPlayerId);
    if (!runtimePlayer) return false;
    const incoming = adaptPlayer(incomingPlayer, runtimePlayer.playerId);
    const substituted = Boolean(this.engine.substitute(side, runtimePlayer.playerId, incoming));
    if (substituted) this.synchronizePresentation();
    return substituted;
  }

  getFuryState(side: 0 | 1): LiveFuryState {
    const fury = this.engine.state.fury[side];
    return {
      active: fury.active,
      used: fury.used,
      remainingSeconds: fury.active ? Math.max(0, (fury.endsAt ?? this.engine.state.time) - this.engine.state.time) : 0,
    };
  }

  /**
   * The club data stores only the TP maximum. Tactical UI must ask the live
   * engine for the mutable value, otherwise a player who has just used a
   * technique misleadingly appears to still have a full bar.
   */
  getPlayerTp(playerId: string): LivePlayerTpState | undefined {
    const player = this.engine.state.players.find((candidate) => (
      candidate.sourcePlayerId ?? candidate.playerId
    ) === playerId);
    return player ? { current: player.tpCurrent, max: player.tpMax } : undefined;
  }

  get currentSecond(): number {
    return this.engine.state.time;
  }

  /**
   * The fixed snapshots are ideal for a completed-match archive, but a live
   * screen cannot wait a whole simulated second before redrawing a player.
   * Expose the exact current engine state for the broadcast layer; historical
   * snapshots remain untouched for results and replay data.
   */
  getPresentationFrame(): WorldFrame {
    const state = this.engine.state;
    return adaptSnapshot(this.engine.getSnapshot(), sourcePlayerIdsFromRuntime(state.players));
  }

  getResult(): MatchResult {
    this.synchronizePresentation();
    const result = this.engine.getResult();
    const possession = result.state.stats.possessionSeconds;
    const totalPossession = possession[0] + possession[1];
    const homePossession = totalPossession > 0 ? Math.round(possession[0] / totalPossession * 100) : 50;
    return {
      simulationVersion: result.simulationVersion,
      seed: this.seed,
      home: this.home,
      away: this.away,
      homeScore: result.homeScore,
      awayScore: result.awayScore,
      events: this.events,
      frames: this.frames,
      decisionLogs: result.state.decisionLogs,
      stats: {
        possession: [homePossession, 100 - homePossession],
        shots: [...result.state.stats.shots],
        shotsOnTarget: [...result.state.stats.shotsOnTarget],
        passes: [...result.state.stats.passes],
        deflections: result.state.stats.deflections,
        rebounds: result.state.stats.rebounds,
        foulsCommitted: [...result.state.stats.foulsCommitted],
        offsides: [...result.state.stats.offsides],
        restarts: {
          throwIns: [...result.state.stats.restarts.throwIns],
          corners: [...result.state.stats.restarts.corners],
          goalKicks: [...result.state.stats.restarts.goalKicks],
          freeKicks: [...result.state.stats.restarts.freeKicks],
          penalties: [...result.state.stats.restarts.penalties],
        },
      },
    };
  }

  private synchronizePresentation(): void {
    const state = this.engine.state;
    const sourcePlayerIds = sourcePlayerIdsFromRuntime(state.players);
    while (this.eventCursor < state.events.length) {
      const event = adaptEvent(state.events[this.eventCursor++], sourcePlayerIds);
      if (event) this.events.push(event);
    }
    while (this.snapshotCursor < state.snapshots.length) {
      this.frames.push(adaptSnapshot(state.snapshots[this.snapshotCursor++], sourcePlayerIds));
    }
  }
}

export function createLiveMatchSession(home: Team, away: Team, seed: number, controlledSide: 0 | 1): LiveMatchSession {
  return new LiveMatchSession(home, away, seed, controlledSide);
}

function adaptCompleteResult(result: EngineMatchResult, home: Team, away: Team, seed: number): MatchResult {
  const sourcePlayerIds = sourcePlayerIdsFromRuntime(result.state.players);
  const events = result.events.flatMap((event) => {
    const adapted = adaptEvent(event, sourcePlayerIds);
    return adapted ? [adapted] : [];
  });
  const frames = result.snapshots.map((snapshot) => adaptSnapshot(snapshot, sourcePlayerIds));
  const possession = result.state.stats.possessionSeconds;
  const totalPossession = possession[0] + possession[1] || 1;
  const homePossession = Math.round(possession[0] / totalPossession * 100);
  return {
    simulationVersion: result.simulationVersion,
    seed,
    home,
    away,
    homeScore: result.homeScore,
    awayScore: result.awayScore,
    events,
    frames,
    decisionLogs: result.state.decisionLogs,
    stats: {
      possession: [homePossession, 100 - homePossession],
      shots: result.state.stats.shots,
      shotsOnTarget: result.state.stats.shotsOnTarget,
      passes: result.state.stats.passes,
      deflections: result.state.stats.deflections,
      rebounds: result.state.stats.rebounds,
      foulsCommitted: result.state.stats.foulsCommitted,
      offsides: result.state.stats.offsides,
      restarts: result.state.stats.restarts,
    },
  };
}

function adaptEvent(event: EngineMatchEvent, sourcePlayerIds: ReadonlyMap<string, string>): MatchEvent | undefined {
  const type = PRESENTABLE_EVENTS[event.type];
  if (!type || (event.type === 'TACKLE' && !event.success)) return undefined;
  return {
    second: event.second,
    type,
    team: event.team,
    actorId: event.actorSourcePlayerId ?? presentationPlayerId(event.actorId, sourcePlayerIds),
    actorAlias: event.actorAlias,
    targetId: event.targetId ? event.targetSourcePlayerId ?? presentationPlayerId(event.targetId, sourcePlayerIds) : undefined,
    targetAlias: event.targetAlias,
    techniqueId: event.techniqueId,
    passType: event.passType,
    restartType: event.restartType,
    message: event.message,
    homeScore: event.homeScore,
    awayScore: event.awayScore,
  };
}

function adaptSnapshot(snapshot: EngineMatchSnapshot, sourcePlayerIds: ReadonlyMap<string, string>): WorldFrame {
  return {
    second: snapshot.second,
    players: snapshot.players.map(({ sourcePlayerId, ...player }) => ({
      ...player,
      id: sourcePlayerId ?? presentationPlayerId(player.id, sourcePlayerIds),
      element: player.element as ElementType,
    })),
    ball: {
      ...snapshot.ball,
      targetPlayerId: snapshot.ball.targetPlayerId
        ? snapshot.ball.targetSourcePlayerId ?? presentationPlayerId(snapshot.ball.targetPlayerId, sourcePlayerIds)
        : undefined,
    },
    possession: snapshot.possession,
    teamPhases: [...snapshot.teamPhases],
    debug: {
      decisions: snapshot.debug.decisions.map((decision) => ({
        ...decision,
        actorId: decision.actorSourcePlayerId,
        nearestOpponentId: decision.nearestOpponentId
          ? decision.nearestOpponentSourcePlayerId ?? presentationPlayerId(decision.nearestOpponentId, sourcePlayerIds)
          : undefined,
      })),
    },
    fury: [...snapshot.fury.active],
  };
}

function sourcePlayerIdsFromRuntime(players: readonly { playerId: string; sourcePlayerId?: string }[]) {
  return new Map(players.map((player) => [player.playerId, player.sourcePlayerId ?? player.playerId]));
}

function presentationPlayerId(matchPlayerId: string, sourcePlayerIds: ReadonlyMap<string, string>) {
  return sourcePlayerIds.get(matchPlayerId) ?? matchPlayerId;
}
